import { useRef } from 'react';
import { useLocation } from 'react-router';
import { useSelector } from 'react-redux';
import { Skeleton } from 'ui/skeleton';
import { kebabCase } from 'utils/stringHelpers';
import { MessageMedium } from 'modules/graphql/types';
import {
  metricSentenceCase,
  getChartActions,
  getChartExportingOptions,
  formatNumberGivenValueType,
} from 'modules/productAnalytics/utils';

import { AudienceRetentionStackChart } from './AudienceRetentionStackChart';
import { MetricBreakdownCard } from './MetricBreakdownCard';

import {
  MetricBreakdownItem,
  SkeletonWrapper,
  MetricBreakdownHeaderTitle,
  MetricBreakdownHeaderValue,
  MetricBreakdownSecondaryListMetricName,
  MetricBreakdownSubMetricValue,
  OverviewCardContainer,
  RetentionOverviewSmallMetrics,
  MetricBreakdownMainMetric,
  OverviewRow,
  GraphContainer,
  StyledDivider,
} from './Styled';
import { RETENTION_OVERVIEW_NICENAME, RETENTION_GRAPH_NICENAME } from '../constants';
import {
  selectAnalyticsMetricsValueTypes,
  selectCampaignAnalyticsStatsForTimerange,
  selectIsLoading,
  selectMessageMedium,
} from '../selectors';
import { parseMetricsForBreakdown, parseMetricsForRetentionGraph } from '../utils';
import { MetricDefinitionWrapper } from './MetricDefinitionWrapper';

type Props = {
  hideLink?: boolean;
};

export const AudienceRetentionOverview = ({ hideLink }: Props) => {
  const isLoading = useSelector(selectIsLoading);
  const { search } = useLocation();
  const statsForTimeRange = useSelector(selectCampaignAnalyticsStatsForTimerange);
  const messageMedium = useSelector(selectMessageMedium);
  const valueTypes = useSelector(selectAnalyticsMetricsValueTypes);
  const chartRef = useRef();
  const chartId = 'engagementOverviewChart';
  const chartActions = getChartActions(chartId, chartRef);

  const retentionOverview = parseMetricsForBreakdown(
    RETENTION_OVERVIEW_NICENAME[messageMedium as MessageMedium] || [],
    statsForTimeRange?.results,
  );
  const [retentionOverviewLeft, ...retentionOverviewRight] = retentionOverview;
  const retentionGraphData = parseMetricsForRetentionGraph(
    RETENTION_GRAPH_NICENAME[messageMedium as MessageMedium],
    statsForTimeRange?.results,
  );

  return (
    <MetricBreakdownCard
      title={'Audience retention'}
      linkout={
        !hideLink
          ? {
              href: '/analytics/campaignPerformance/retention',
              search,
            }
          : undefined
      }
      menuOptions={getChartExportingOptions(chartId)}
      handleSelectionChange={(key) => chartActions[key]()}
      data-test="audience-overview"
    >
      {isLoading ? (
        <SkeletonWrapper height={576} padding={24}>
          <Skeleton />
        </SkeletonWrapper>
      ) : (
        <OverviewCardContainer>
          {retentionOverview.length > 0 && (
            <OverviewRow>
              <MetricBreakdownMainMetric>
                <MetricBreakdownHeaderTitle data-test="metric-title-audience-retention">
                  <MetricDefinitionWrapper metricName={retentionOverviewLeft?.[0].name}>
                    {metricSentenceCase(retentionOverviewLeft?.[0].name)}
                  </MetricDefinitionWrapper>
                </MetricBreakdownHeaderTitle>
                <MetricBreakdownHeaderValue data-test="metric-value-audience-retention">
                  {retentionOverviewLeft?.[0].value}
                </MetricBreakdownHeaderValue>
              </MetricBreakdownMainMetric>

              <RetentionOverviewSmallMetrics>
                {retentionOverviewRight.map((arr: Record<string, any>[]) => {
                  const metric = arr[0] || {};
                  const { name, value, key } = metric;
                  return (
                    <MetricBreakdownItem key={name}>
                      <MetricBreakdownSecondaryListMetricName
                        data-test={`metric-title-${kebabCase(name)}`}
                      >
                        <MetricDefinitionWrapper metricName={name}>
                          {metricSentenceCase(name)}
                        </MetricDefinitionWrapper>
                      </MetricBreakdownSecondaryListMetricName>
                      <MetricBreakdownSubMetricValue data-test={`metric-value-${kebabCase(name)}`}>
                        {formatNumberGivenValueType(valueTypes[key], value) || 'N/A'}
                      </MetricBreakdownSubMetricValue>
                    </MetricBreakdownItem>
                  );
                })}
              </RetentionOverviewSmallMetrics>
            </OverviewRow>
          )}
          <StyledDivider />
          <GraphContainer>
            {retentionGraphData && (
              <AudienceRetentionStackChart {...retentionGraphData} ref={chartRef} />
            )}
          </GraphContainer>
        </OverviewCardContainer>
      )}
    </MetricBreakdownCard>
  );
};
